/**
 * BreadcrumbContext — lets any page override the auto-generated label
 * for a specific URL path segment.
 *
 * Usage in a detail page:
 *
 *   import { useBreadcrumbOverride } from '@/components/BreadcrumbContext'
 *
 *   function TenderDetailsPage() {
 *     const { tenderId } = useParams()
 *     const { data: tender } = useTender(tenderId)
 *     useBreadcrumbOverride(
 *       tender ? `/tenders/${tender.id}` : null,
 *       tender?.tender_code,
 *     )
 *     // ...
 *   }
 *
 * The Breadcrumbs component reads this context and substitutes the
 * provided label in place of "#539" or similar raw-id segments.
 *
 * Overrides are scoped to the lifetime of the registering component —
 * unmounting clears them automatically. The map is keyed by absolute
 * path ("/tenders/539") so multiple detail pages can register their
 * overrides independently without conflicts.
 */
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'

type OverrideMap = Record<string, string>

interface BreadcrumbContextValue {
  overrides: OverrideMap
  setOverride: (path: string, label: string) => void
  clearOverride: (path: string) => void
}

const Ctx = createContext<BreadcrumbContextValue>({
  overrides: {},
  setOverride: () => {},
  clearOverride: () => {},
})

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<OverrideMap>({})

  const setOverride = useCallback((path: string, label: string) => {
    setOverrides((prev) => {
      if (prev[path] === label) return prev
      return { ...prev, [path]: label }
    })
  }, [])

  const clearOverride = useCallback((path: string) => {
    setOverrides((prev) => {
      if (!(path in prev)) return prev
      const next = { ...prev }
      delete next[path]
      return next
    })
  }, [])

  return (
    <Ctx.Provider value={{ overrides, setOverride, clearOverride }}>
      {children}
    </Ctx.Provider>
  )
}

export function useBreadcrumbContext(): BreadcrumbContextValue {
  return useContext(Ctx)
}

/**
 * Convenience hook: register an override for a specific path, then
 * auto-clear on unmount or when path/label changes.
 *
 * Pass null for either argument to skip registration (useful while
 * the entity is still loading from API).
 */
export function useBreadcrumbOverride(
  path: string | null | undefined,
  label: string | null | undefined,
): void {
  const { setOverride, clearOverride } = useBreadcrumbContext()

  useEffect(() => {
    if (!path || !label) return
    setOverride(path, label)
    return () => clearOverride(path)
  }, [path, label, setOverride, clearOverride])
}